import Helpers from '../base/Helpers';
import Response from '../base/Response';
import Settings from '../models/Settings';
import AuthTokenization from '../requests/AuthTokenization';
import LookupContinueTransaction from '../requests/LookupContinueTransaction';
import LookupTransaction from '../requests/LookupTransaction';
import PaymentTransaction from '../requests/PaymentTransaction';
import Environment from '../resources/Environment';
import FailureResponse from '../responses/FailureResponse';
import CardinalAuthentication from '../services/CardinalAuthentication';
import Loading from './PixelPayLoading';
import Modal from './PixelPayModal';

declare global {
	interface Window {
		Cardinal: any;
	}
}

export default class CardinalManager {
	/**
	 * Settings service model
	 */
	settings: Settings;

	/**
	 * Cardinal authentication service
	 */
	service: CardinalAuthentication;

	/**
	 * Payment transaction
	 */
	transaction: PaymentTransaction;

	/**
	 * PixelPay transaction authentication identifier
	 */
	identifier?: string;

	/**
	 * Direct API URL for data collection
	 */
	collection_url?: string;

	/**
	 * Direct API transaction flag
	 */
	direct_api: boolean = false;

	/**
	 * Listener for Cardinal Direct API messages
	 */
	listen_direct_message?: (event: MessageEvent) => Promise<void>;

	/**
	 * Resolve promise when direct API transaction is complete
	 */
	direct_api_resolve?: (value: Response | PromiseLike<Response>) => void;

	/**
	 * Resolve promise when direct API transaction fails
	 */
	direct_api_reject?: (reason?: any) => void;

	/**
	 * Initialize service
	 */
	constructor(settings: Settings, request: PaymentTransaction) {
		this.settings = settings;
		this.transaction = request;
		this.service = new CardinalAuthentication(this.settings);
	}

	/**
	 * Clean all Cardinal instances
	 */
	static clearAllEvents() {
		if (Helpers.checkIsBrowser()) {
			Loading.hide();

			if (typeof window.Cardinal != 'undefined') {
				window.Cardinal.off('payments.setupComplete');
				window.Cardinal.off('payments.validated');

				window.Cardinal.onSetupCompleteBusy = false;
				window.Cardinal.onValidatedBusy = false;
			}
		}
	}

	/**
	 * Initialize manager
	 *
	 * @param jwt
	 * @param url
	 * @return
	 */
	async init(jwt: string, url?: string | null): Promise<Response> {
		if (url) {
			this.direct_api = true;
			this.collection_url = url;

			const secure_form = Helpers.addSecureForm(jwt, url);
			const result = await this.startCardinalDirectApiTransaction(secure_form).finally(() => {
				if (this.listen_direct_message) {
					window.removeEventListener('message', this.listen_direct_message);
				}

				Helpers.removeElementById('step-up-form');
				Helpers.removeElementById('cardinal_collection_iframe');
				Helpers.removeElementById('cardinal_collection_form');

				Modal.hide();
				Loading.hide();
			});

			return result;
		}

		const testing = this.settings.environment === Environment.SANDBOX || this.settings.environment === Environment.STAGING;
		const songbirdEnv = testing ? 'stag' : '';
		await Helpers.addScriptToWebsite(`https://songbird${songbirdEnv}.cardinalcommerce.com/edge/v1/songbird.js`);

		window.Cardinal.configure({
			payment: {
				framework: 'inline'
			},
			logging: {
				level: testing ? 'on' : 'off'
			},
		});

		window.Cardinal.onSetupCompleteBusy = false;
		window.Cardinal.onValidatedBusy = false;

		return await this.startCardinalTransaction(jwt, this);
	}

	/**
	 * Cardinal transacction starting service
	 */
	startCardinalTransaction(jwt_setup: string, manager: CardinalManager): Promise<Response> {
		return new Promise((resolve, reject) => {
			try {
				window.Cardinal.on('payments.setupComplete', async (setupCompleteData: any) => {
					if (window.Cardinal.onSetupCompleteBusy) {
						return console.warn('Cardinal event "payments.setupComplete" is currently busy.');
					}

					window.Cardinal.onSetupCompleteBusy = true;

					if (typeof setupCompleteData.sessionId == 'undefined'
						|| (typeof setupCompleteData.sessionId != 'undefined'
							&& (!setupCompleteData.sessionId || setupCompleteData.sessionId == ''))) {
						throw new Error('Cardinal initial setup error.');
					}

					const lookup = new LookupTransaction();
					lookup.fromPaymentTransaction(manager.transaction);
					lookup.setRequiredEMVFields();
					lookup.reference = setupCompleteData.sessionId;

					const lookup_response = await manager.service.authenticationLookup(lookup);
					manager.identifier = lookup_response?.data?.identifier;
					manager.validationLookupResponse(lookup_response, resolve);

					window.Cardinal.onSetupCompleteBusy = false;
				});

				window.Cardinal.on('payments.validated', async (data: any, jwt: any) => {
					if (data?.ActionCode !== 'ERROR' && window.Cardinal.onValidatedBusy) {
						return console.warn('Cardinal event "payments.validated" is currently busy.');
					}

					window.Cardinal.onValidatedBusy = true;

					if (typeof jwt != 'undefined' && jwt && jwt != '') {
						const lookup_continue = new LookupContinueTransaction();
						lookup_continue.payload = jwt;
						lookup_continue.identifier = manager.identifier;
						lookup_continue.card_token = manager.transaction.card_token;
						lookup_continue.authenticate_token = manager.transaction instanceof AuthTokenization;

						const continue_response = await manager.service.authenticationContinue(lookup_continue);

						if (lookup_continue.authenticate_token) {
							CardinalManager.clearAllEvents();
							return resolve(continue_response);
						}

						const result = await manager.service.retryTransaction(manager.transaction);

						CardinalManager.clearAllEvents();
						resolve(result);
					} else if (data.ActionCode == 'NOACTION') {
						const result = await manager.service.retryTransaction(manager.transaction);

						CardinalManager.clearAllEvents();
						resolve(result);
					} else {
						const failure = new FailureResponse();
						failure.setStatus(520);
						failure.message = `Cardinal validation failed. ${data.ErrorDescription} (${data.ErrorNumber})`;
						failure.data = {};

						CardinalManager.clearAllEvents();
						resolve(failure);
					}

					window.Cardinal.onValidatedBusy = false;
				});

				window.Cardinal.on('ui.close', () => {
					Modal.hide();
				});

				window.Cardinal.on('ui.inline.setup', (htmlTemplate: any, details: any, resolve: any, reject: any) => {
					try {
						let container;

						if (htmlTemplate !== undefined && details !== undefined) {
							switch (details.paymentType) {
								case 'CCA':
									switch (details.data.mode) {
										case 'static':
											Modal.show();
											let el = document.querySelector('.pixelpay-modal');

											if (document.querySelector('[without-sdk-modal]')) {
												el = document.querySelector('[without-sdk-modal]');
											}

											container = el?.querySelector('.pixelpay-modal-container') || el;
											el?.setAttribute('data-size', details.data.challengeWindowSizeId);
											break;

										case 'suppress':
											Modal.hide();
											container = document.querySelector('.pixelpay-modal-container');
											break;
										default:
											throw new Error('Unsupported inline mode found [' + details.data.mode + ']');
									}

									break;
								default:
									throw new Error('Unsupported inline payment type found [' + details.paymentType + ']');
							}

							if (container) {
								container.innerHTML = htmlTemplate
							}

							resolve();
						} else {
							throw new Error('Unable to process request due to invalid arguments');
						}
					} catch (error) {
						reject(error);
					}
				});

				window.Cardinal.setup('init', { jwt: jwt_setup });
			} catch (err) {
				CardinalManager.clearAllEvents();
				reject(err);
			}
		});
	}

	/**
	 * Start Cardinal Direct API transaction
	 *
	 * @param secure_form
	 * @return
	 */
	private startCardinalDirectApiTransaction(secure_form: HTMLFormElement): Promise<Response> {
		return new Promise((resolve, reject) => {
			this.direct_api_resolve = resolve;
			this.direct_api_reject = reject;

			this.listen_direct_message = async (event: MessageEvent): Promise<void> => {
				try {
					if (event?.origin === this.settings.endpoint && event.data?.name === 'pixelpay_continue') {
						return await this.handleDirectApiContinue(event);
					}

					if (event.origin !== new URL(this.collection_url!).origin) {
						return;
					}

					const event_data = Helpers.safeParse(event.data);
					console.log('Received message from Cardinal Direct API:', event_data);
					if (event_data?.MessageType === 'profile.completed') {
						const checkMethod = async (startTime = Date.now()) => {
							const methodUrl = event_data?.Payload?.DataSources?.MethodUrl;
							const completed = event_data?.Status ?? methodUrl?.Completed;
							const elapsed = Date.now() - startTime;
							const delay = Math.min(500, (10000 - elapsed));

							if (elapsed > 10000 || completed !== false) {
								return await this.handleDirectApiLookup(event_data);
							}

							setTimeout(() => checkMethod(startTime), delay);
						};

						checkMethod();
					}
				} catch (error) {
					const failure = new FailureResponse();
					failure.setStatus(520);
					failure.message = 'No se pudo procesar la transacción, intenta de nuevo con otro dispositivo o navegador (1000)';
					failure.data = Helpers.serialize(error);

					reject(failure);
				}
			};

			window.addEventListener('message', this.listen_direct_message, false);
			secure_form.submit();
		});
	}

	/**
	 * Handle continue message from iframe.
	 *
	 * @param event
	 */
	async handleDirectApiContinue(event: MessageEvent): Promise<void> {
		const error = event.data?.error;

		if (error) {
			const failure = new FailureResponse();
			failure.setStatus(520);
			failure.message = 'No se pudo procesar la transacción, intenta de nuevo con otro dispositivo o navegador (1001)';
			failure.data = Helpers.serialize(error);

			return this.direct_api_reject!(failure);
		}

		const identifier = event.data?.identifier;

		if (!identifier) {
			const failure = new FailureResponse();
			failure.setStatus(520);
			failure.message = 'Identifier not found in the response.';
			failure.data = {};

			return this.direct_api_reject!(failure);
		}

		this.identifier = identifier;

		Modal.hide();
		const result = await this.service.retryTransaction(this.transaction);
		return this.direct_api_resolve!(result);
	}

	/**
	 * Initiate lookup message with iframe.
	 *
	 * @param event_data
	 * @return
	 */
	async handleDirectApiLookup(event_data: any): Promise<void> {
		const sessionId = event_data?.SessionId || event_data?.Payload?.SessionId;

		if (!sessionId) {
			const failure = new FailureResponse();
			failure.setStatus(520);
			failure.message = 'SessionId not found in the response.';
			failure.data = {};

			return this.direct_api_reject!(failure);
		}

		const lookup = new LookupTransaction();
		lookup.fromPaymentTransaction(this.transaction);
		lookup.setRequiredEMVFields();
		lookup.card_cvv = this.transaction.card_cvv;
		lookup.reference = sessionId;

		const lookup_response = await this.service.authenticationLookup(lookup);
		this.identifier = lookup_response?.data?.identifier;
		await this.validationLookupResponse(lookup_response, this.direct_api_resolve!);

		if (lookup_response.action === 'continue') {
			const { continue_url, continue_payload, window_size } = lookup_response.data;

			this.submitChallengeForm(continue_url, continue_payload, window_size, this.settings.auth_key);
		}
	}

	/**
	 * Verify lookup response and run Cardinal attempt
	 *
	 * @param response
	 * @param resolve
	 */
	async validationLookupResponse(response: Response, resolve: (value: Response | PromiseLike<Response>) => void) {
		this.transaction.authentication_identifier = this.identifier;

		if (!response.success || ['token_auth_success', 'token_auth_failed'].includes(response.action!)) {
			CardinalManager.clearAllEvents();
			return resolve(response);
		}

		if (response.action != 'continue') {
			const result = await this.service.retryTransaction(this.transaction);
			CardinalManager.clearAllEvents();
			return resolve(result);
		}

		if (!this.direct_api) {
			window.Cardinal.continue('cca', {
				AcsUrl: response.data.continue_url,
				Payload: response.data.continue_payload,
			}, {
				OrderDetails: {
					TransactionId: response.data.transaction_id,
					OrderNumber: this.transaction.order_id,
					CurrencyCode: this.transaction.order_currency,
					// Amount: this.transaction.order_amount,
				},
			});
		}
	}

	/**
	 * Submit Form to Iframe for Step-Up Challenge
	 *
	 * @param continue_url
	 * @param jwt
	 * @param window_size
	 * @param custom_data
	 */
	private submitChallengeForm(
		continue_url: string,
		jwt: string,
		window_size?: string,
		custom_data?: string,
	): void {
		Modal.show();

		window_size = window_size ?? '02';
		const { width, height } = Helpers.convertWindowSizeToDimensions(window_size);

		let iframe: HTMLIFrameElement | null = document.querySelector('iframe[name="step-up-iframe"]');

		if (!iframe) {
			iframe = document.createElement('iframe');
			iframe.name = 'step-up-iframe';
		}

		iframe.width = width;
		iframe.height = height;
		iframe.style.border = 'none';
		document.body.appendChild(iframe);

		let el = document.querySelector('.pixelpay-modal');
		if (document.querySelector('[without-sdk-modal]')) {
			el = document.querySelector('[without-sdk-modal]');
		}

		const container = el?.querySelector('.pixelpay-modal-container') || el;

		el?.setAttribute('data-size', window_size);

		if (! container) {
			return;
		}

		container.appendChild(iframe);

		const form = document.createElement('form');
		form.id = 'step-up-form';
		form.target = iframe.name;
		form.method = 'POST';
		form.action = continue_url;

		const jwtInput = document.createElement('input');
		jwtInput.type = 'hidden';
		jwtInput.name = 'JWT';
		jwtInput.value = jwt;
		form.appendChild(jwtInput);

		if (custom_data) {
			const input = document.createElement('input');
			input.type = 'hidden';
			input.name = 'MD';
			input.value = custom_data;
			form.appendChild(input);
		}

		document.body.appendChild(form);
		form.submit();
	}
}
